import type { Condition } from '@repo/api-gw-sdk';
import {
  ConditionOperatorEnum,
  Environment,
  ListOperators,
  NetworkClassification,
  Provider,
  ResourceType,
  ScalarOperators,
  StringOperators,
} from '@repo/api-gw-sdk';

import {
  fromTagKeyValues,
  toTagKeyValues,
} from '@/components/queryBuilder/tags/tagKeyValueUtils';

// ---------------------------------------------------------------------------
// Operator lookup maps
// ---------------------------------------------------------------------------

export const conditionOperatorByScalarOperator: Record<
  ScalarOperators,
  ConditionOperatorEnum
> = {
  [ScalarOperators.IN_OPERATOR]: ConditionOperatorEnum.In,
  [ScalarOperators.NOT_IN_OPERATOR]: ConditionOperatorEnum.NotIn,
  [ScalarOperators.SCALAR_OPERATOR_UNSPECIFIED]: ConditionOperatorEnum.In,
};

export const conditionOperatorByListOperator: Record<
  ListOperators,
  ConditionOperatorEnum
> = {
  [ListOperators.CONTAINS_ANY_OF_OPERATOR]: ConditionOperatorEnum.ContainsAnyOf,
  [ListOperators.CONTAINS_NONE_OF_OPERATOR]:
    ConditionOperatorEnum.ContainsNoneOf,
  [ListOperators.CONTAINS_ALL_OF_OPERATOR]: ConditionOperatorEnum.ContainsAllOf,
  [ListOperators.LIST_OPERATOR_UNSPECIFIED]:
    ConditionOperatorEnum.ContainsAnyOf,
};

const conditionOperatorByStringOperator: Record<
  StringOperators,
  ConditionOperatorEnum
> = {
  [StringOperators.STRING_IN_OPERATOR]: ConditionOperatorEnum.In,
  [StringOperators.STRING_NOT_IN_OPERATOR]: ConditionOperatorEnum.NotIn,
  [StringOperators.STRING_CONTAINS_OPERATOR]: ConditionOperatorEnum.Contains,
  [StringOperators.STRING_NOT_CONTAINS_OPERATOR]:
    ConditionOperatorEnum.NotContains,
  [StringOperators.STRING_STARTS_WITH_OPERATOR]:
    ConditionOperatorEnum.StartsWith,
  [StringOperators.STRING_NOT_STARTS_WITH_OPERATOR]:
    ConditionOperatorEnum.NotStartsWith,
  [StringOperators.STRING_ENDS_WITH_OPERATOR]: ConditionOperatorEnum.EndsWith,
  [StringOperators.STRING_NOT_ENDS_WITH_OPERATOR]:
    ConditionOperatorEnum.NotEndsWith,
  [StringOperators.STRING_OPERATOR_UNSPECIFIED]: ConditionOperatorEnum.In,
};

// ---------------------------------------------------------------------------
// Operator converters (Condition operator → SDK operator)
// ---------------------------------------------------------------------------

export const toScalarOperator = (
  operator: string
): ScalarOperators | undefined => {
  switch (operator) {
    case ConditionOperatorEnum.In:
    case ConditionOperatorEnum.ContainsAnyOf:
      return ScalarOperators.IN_OPERATOR;
    case ConditionOperatorEnum.NotIn:
    case ConditionOperatorEnum.ContainsNoneOf:
      return ScalarOperators.NOT_IN_OPERATOR;
    default:
      return undefined;
  }
};

export const toStringOperator = (operator: string) => {
  switch (operator) {
    case ConditionOperatorEnum.In:
    case ConditionOperatorEnum.ContainsAnyOf:
      return StringOperators.STRING_IN_OPERATOR;
    case ConditionOperatorEnum.NotIn:
    case ConditionOperatorEnum.ContainsNoneOf:
      return StringOperators.STRING_NOT_IN_OPERATOR;
    case ConditionOperatorEnum.Contains:
      return StringOperators.STRING_CONTAINS_OPERATOR;
    case ConditionOperatorEnum.NotContains:
      return StringOperators.STRING_NOT_CONTAINS_OPERATOR;
    case ConditionOperatorEnum.StartsWith:
      return StringOperators.STRING_STARTS_WITH_OPERATOR;
    case ConditionOperatorEnum.NotStartsWith:
      return StringOperators.STRING_NOT_STARTS_WITH_OPERATOR;
    case ConditionOperatorEnum.EndsWith:
      return StringOperators.STRING_ENDS_WITH_OPERATOR;
    case ConditionOperatorEnum.NotEndsWith:
      return StringOperators.STRING_NOT_ENDS_WITH_OPERATOR;
    default:
      return undefined;
  }
};

export const toListOperator = (operator: string): ListOperators | undefined => {
  switch (operator) {
    case ConditionOperatorEnum.In:
    case ConditionOperatorEnum.ContainsAnyOf:
      return ListOperators.CONTAINS_ANY_OF_OPERATOR;
    // `Contains` is how conditions stored before the all-of operator existed
    // spell it, so both reach the same SDK operator.
    case ConditionOperatorEnum.ContainsAllOf:
    case ConditionOperatorEnum.Contains:
      return ListOperators.CONTAINS_ALL_OF_OPERATOR;
    case ConditionOperatorEnum.NotIn:
    case ConditionOperatorEnum.ContainsNoneOf:
      return ListOperators.CONTAINS_NONE_OF_OPERATOR;
    default:
      return undefined;
  }
};

// ---------------------------------------------------------------------------
// Enum filter helpers
// ---------------------------------------------------------------------------

const filterResourceTypes = (values: string[]): ResourceType[] =>
  Object.values(ResourceType).filter((value) => values.includes(value));

const filterProviders = (values: string[]): Provider[] =>
  Object.values(Provider).filter((value) => values.includes(value));

const filterEnvironments = (values: string[]): Environment[] =>
  Object.values(Environment).filter((value) => values.includes(value));

const filterNetworkClassifications = (
  values: string[]
): NetworkClassification[] =>
  Object.values(NetworkClassification).filter((value) =>
    values.includes(value)
  );

// ---------------------------------------------------------------------------
// Shared condition → expression helpers
// ---------------------------------------------------------------------------

export const conditionToResourceTypeExpression = (
  values: string[],
  operator: ScalarOperators
) => ({
  resourceType: { operator, resourceTypes: filterResourceTypes(values) },
});

export const conditionToDataClassesExpression = (
  values: string[],
  operator: ListOperators
) => ({ dataClasses: { operator, dataClasses: values } });

export const conditionToEnvironmentExpression = (
  values: string[],
  operator: ScalarOperators
) => ({ environment: { operator, environments: filterEnvironments(values) } });

export const conditionToAppsExpression = (
  values: string[],
  operator: ListOperators
) => ({ apps: { operator, apps: values } });

export const conditionToCloudProviderExpression = (
  values: string[],
  operator: ScalarOperators
) => ({ cloudProvider: { operator, cloudProviders: filterProviders(values) } });

export const conditionToAccountIdExpression = (
  values: string[],
  operator: ScalarOperators
) => ({ accountId: { operator, accountIds: values } });

export const conditionToSourceRegionExpression = (
  values: string[],
  operator: ScalarOperators
) => ({ sourceRegion: { operator, regions: values } });

export const conditionToVpcExpression = (
  values: string[],
  operator: ScalarOperators
) => ({ vpc: { operator, vpcs: values } });

export const conditionToSubnetsExpression = (
  values: string[],
  operator: ListOperators
) => ({ subnets: { operator, subnets: values } });

export const conditionToResourceGroupNameExpression = (
  values: string[],
  operator: ScalarOperators
) => ({ resourceGroupName: { operator, resourceGroupNames: values } });

export const conditionToResourceNameExpression = (
  values: string[],
  operator: StringOperators
) => ({ resourceName: { operator, resourceNames: values } });

export const conditionToResourceIdExpression = (
  values: string[],
  operator: ScalarOperators
) => ({ resourceId: { operator, resourceIds: values } });

export const conditionToTagKeysExpression = (
  values: string[],
  operator: ListOperators
) => ({ tagKeys: { operator, tagKeys: values } });

export const conditionToTagKeyValuesExpression = (
  values: string[],
  operator: ListOperators
) => ({ tagKeyValues: { operator, tagKeyValues: toTagKeyValues(values) } });

export const conditionToNetworkClassificationExpression = (
  values: string[],
  operator: ScalarOperators
) => ({
  networkClassification: {
    operator,
    networkClassifications: filterNetworkClassifications(values),
  },
});

// ---------------------------------------------------------------------------
// Shared expression → condition helpers
// ---------------------------------------------------------------------------

export const resourceTypeExpressionToCondition = (expression: {
  operator: ScalarOperators;
  resourceTypes: ResourceType[];
}): Condition => ({
  property: 'resourceType',
  operator: conditionOperatorByScalarOperator[expression.operator],
  value: expression.resourceTypes.map(String),
});

export const dataClassesExpressionToCondition = (expression: {
  operator: ListOperators;
  dataClasses: string[];
}): Condition => ({
  property: 'dataClasses',
  operator: conditionOperatorByListOperator[expression.operator],
  value: expression.dataClasses,
});

export const environmentExpressionToCondition = (expression: {
  operator: ScalarOperators;
  environments: Environment[];
}): Condition => ({
  property: 'environment',
  operator: conditionOperatorByScalarOperator[expression.operator],
  value: expression.environments.map(String),
});

export const appsExpressionToCondition = (expression: {
  operator: ListOperators;
  apps: string[];
}): Condition => ({
  property: 'apps',
  operator: conditionOperatorByListOperator[expression.operator],
  value: expression.apps,
});

export const cloudProviderExpressionToCondition = (expression: {
  operator: ScalarOperators;
  cloudProviders: Provider[];
}): Condition => ({
  property: 'cloudProvider',
  operator: conditionOperatorByScalarOperator[expression.operator],
  value: expression.cloudProviders.map(String),
});

export const accountIdExpressionToCondition = (expression: {
  operator: ScalarOperators;
  accountIds: string[];
}): Condition => ({
  property: 'sourceAccountId',
  operator: conditionOperatorByScalarOperator[expression.operator],
  value: expression.accountIds,
});

export const sourceRegionExpressionToCondition = (expression: {
  operator: ScalarOperators;
  regions: string[];
}): Condition => ({
  property: 'sourceRegion',
  operator: conditionOperatorByScalarOperator[expression.operator],
  value: expression.regions,
});

export const vpcExpressionToCondition = (expression: {
  operator: ScalarOperators;
  vpcs: string[];
}): Condition => ({
  property: 'vpc',
  operator: conditionOperatorByScalarOperator[expression.operator],
  value: expression.vpcs,
});

export const subnetsExpressionToCondition = (expression: {
  operator: ListOperators;
  subnets: string[];
}): Condition => ({
  property: 'subnets',
  operator: conditionOperatorByListOperator[expression.operator],
  value: expression.subnets,
});

export const resourceGroupNameExpressionToCondition = (expression: {
  operator: ScalarOperators;
  resourceGroupNames: string[];
}): Condition => ({
  property: 'resourceGroupName',
  operator: conditionOperatorByScalarOperator[expression.operator],
  value: expression.resourceGroupNames,
});

export const resourceNameExpressionToCondition = (expression: {
  operator: StringOperators;
  resourceNames: string[];
}): Condition => ({
  property: 'resourceName',
  operator: conditionOperatorByStringOperator[expression.operator],
  value: expression.resourceNames,
});

export const resourceIdExpressionToCondition = (expression: {
  operator: ScalarOperators;
  resourceIds: string[];
}): Condition => ({
  property: 'resourceId',
  operator: conditionOperatorByScalarOperator[expression.operator],
  value: expression.resourceIds,
});

export const tagKeysExpressionToCondition = (expression: {
  operator: ListOperators;
  tagKeys: string[];
}): Condition => ({
  property: 'tagKeys',
  operator: conditionOperatorByListOperator[expression.operator],
  value: expression.tagKeys,
});

export const tagKeyValuesExpressionToCondition = (expression: {
  operator: ListOperators;
  tagKeyValues: { key: string; value?: string | null }[];
}): Condition => ({
  property: 'tagKeyValues',
  operator: conditionOperatorByListOperator[expression.operator],
  value: fromTagKeyValues(expression.tagKeyValues),
});

export const networkClassificationExpressionToCondition = (expression: {
  operator: ScalarOperators;
  networkClassifications: NetworkClassification[];
}): Condition => ({
  property: 'networkClassification',
  operator: conditionOperatorByScalarOperator[expression.operator],
  value: expression.networkClassifications.map(String),
});
